import React, { useState, useEffect } from "react";
import { getAllCourseAnnouncements } from "../../../../../services/announcement.service";
import { useParams } from "react-router-dom";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, Search, Clock, User, Calendar, ChevronDown } from "lucide-react";
import {
  card,
  emptyState,
  microLabel,
  bodySecondary,
  cardTitle,
  pageTitle,
  accentBarSection,
  accentBarCard,
  badgeBase,
  dividerInner,
  surfaceMuted,
  iconSize,
  motionFadeUp,
  motionStaggerChild,
  motionPageTransition,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import { AnnouncementListSkeleton } from "../../../../StudentDashboard/Components/ui/Skeleton";

const AllAnnouncements = ({ courseID: courseIDProp }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const { courseID: courseIDParam } = useParams();
  const courseID = courseIDProp || courseIDParam;

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await getAllCourseAnnouncements({ courseID });
      setAnnouncements(response.announcements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    let filtered = announcements;

    if (searchTerm) {
      filtered = filtered.filter(
        (announcement) =>
          announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          announcement.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filter === "recent") {
      filtered = [...filtered].sort(
        (a, b) => new Date(b.publishDate) - new Date(a.publishDate)
      );
    }

    setFilteredAnnouncements(filtered);
  }, [searchTerm, filter, announcements]);

  const timeAgo = (dateString) => {
    const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
    const intervals = [
      { label: "year", seconds: 31536000 },
      { label: "month", seconds: 2592000 },
      { label: "week", seconds: 604800 },
      { label: "day", seconds: 86400 },
      { label: "hour", seconds: 3600 },
      { label: "minute", seconds: 60 },
    ];
    for (const { label, seconds: s } of intervals) {
      const count = Math.floor(seconds / s);
      if (count >= 1) return `${count} ${label}${count > 1 ? "s" : ""} ago`;
    }
    return "Just now";
  };

  const groupAnnouncementsByWeek = (list) => {
    const grouped = {};
    list.forEach((announcement) => {
      const publishDate = new Date(announcement.publishDate);
      const weekStart = startOfWeek(publishDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(publishDate, { weekStartsOn: 1 });
      const weekRange = `${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`;
      if (!grouped[weekRange]) grouped[weekRange] = [];
      grouped[weekRange].push(announcement);
    });
    return grouped;
  };

  const groupedAnnouncements = groupAnnouncementsByWeek(filteredAnnouncements);

  return (
    <motion.div {...motionFadeUp} className="space-y-6">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div>
        <h1 className={pageTitle}>All Announcements</h1>
        <div className={cn(accentBarSection, "mt-3")} />
        <p className={cn(bodySecondary, "mt-3")}>
          Stay updated with course communications
        </p>
      </div>

      {/* ── Search and Filter ───────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            className={cn(
              iconSize.md,
              "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
            )}
          />
          <input
            type="text"
            placeholder="Search announcements..."
            className={cn(
              "w-full pl-9 pr-4 py-2 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm",
              dividerInner
            )}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            className={cn(
              "w-full sm:w-44 pl-4 pr-8 py-2 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm appearance-none cursor-pointer",
              dividerInner
            )}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Announcements</option>
            <option value="recent">Most Recent</option>
          </select>
          <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
            <ChevronDown className={cn(iconSize.xs, "text-gray-400")} />
          </div>
        </div>
      </div>

      {/* ── Announcements List ──────────────────────────────────── */}
      <div className="space-y-8">
        {loading ? (
          <AnnouncementListSkeleton />
        ) : Object.keys(groupedAnnouncements).length > 0 ? (
          Object.keys(groupedAnnouncements).map((weekRange, weekIndex) => (
            <motion.div key={weekRange} {...motionStaggerChild(weekIndex)}>
              {/* Week label */}
              <div className="flex items-center gap-2 mb-4">
                <Calendar className={cn(iconSize.sm, "text-accent1")} />
                <h2 className={cn(microLabel, "pb-0")}>{weekRange}</h2>
              </div>
              <div className={cn("h-px mb-4", "bg-gray-200 dark:bg-gray-700")} />

              {/* Announcements for this week */}
              <div className="space-y-4">
                {groupedAnnouncements[weekRange].map((announcement, i) => {
                  const isExpanded = expandedId === announcement._id;
                  return (
                    <motion.div
                      key={announcement._id}
                      className={cn(
                        card,
                        "overflow-hidden cursor-pointer hover:shadow-md transition-all"
                      )}
                      {...motionStaggerChild(i, weekIndex * 0.15)}
                      whileHover={{ y: -2 }}
                      onClick={() =>
                        setExpandedId(isExpanded ? null : announcement._id)
                      }
                    >
                      <div className="flex">
                        {/* Accent bar on left */}
                        <div className="w-1 bg-accent1 shrink-0" />

                        {/* Text content */}
                        <div className="flex-1 p-4 sm:p-5 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className={cn(cardTitle, "truncate")}>
                                {announcement.title}
                              </h3>
                              <div
                                className={cn(
                                  bodySecondary,
                                  "mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1"
                                )}
                              >
                                <span className="flex items-center gap-1">
                                  <Clock className={iconSize.xs} />
                                  {timeAgo(announcement.publishDate)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className={iconSize.xs} />
                                  {format(
                                    new Date(announcement.publishDate),
                                    "MMM d, yyyy 'at' h:mm a"
                                  )}
                                </span>
                                {announcement.publishedBy?.user?.name && (
                                  <span className="flex items-center gap-1">
                                    <User className={iconSize.xs} />
                                    {announcement.publishedBy.user.name}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Time ago badge */}
                            <span
                              className={cn(
                                badgeBase,
                                "shrink-0 bg-accent1/10 text-accent1 border-accent1/30 whitespace-nowrap"
                              )}
                            >
                              {timeAgo(announcement.publishDate)}
                            </span>
                          </div>

                          <p
                            className={cn(
                              "mt-3 text-sm text-gray-600 dark:text-gray-300 leading-relaxed",
                              !isExpanded && "line-clamp-2"
                            )}
                          >
                            {announcement.content}
                          </p>

                          {announcement.content?.length > 150 && (
                            <button
                              className="mt-2 text-xs font-bold text-accent1 hover:text-accent1/80 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedId(
                                  isExpanded ? null : announcement._id
                                );
                              }}
                            >
                              {isExpanded ? "Show less" : "Read more"}
                            </button>
                          )}
                        </div>

                        {/* Image on right */}
                        {announcement.image?.imageUrl && (
                          <div className="hidden sm:block w-48 lg:w-56 shrink-0 border-l border-gray-200 dark:border-gray-700">
                            <img
                              src={announcement.image.imageUrl}
                              alt={announcement.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </div>

                      {/* Mobile image — below content when present */}
                      {announcement.image?.imageUrl && (
                        <div className="sm:hidden border-t border-gray-200 dark:border-gray-700">
                          <img
                            src={announcement.image.imageUrl}
                            alt={announcement.title}
                            className="w-full h-40 object-cover"
                          />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))
        ) : (
          <div className={emptyState}>
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-50 dark:bg-gray-900 rounded-sm flex items-center justify-center">
              <Megaphone
                className={cn(iconSize.empty, "text-gray-400 dark:text-gray-500")}
              />
            </div>
            <p className="font-bold text-gray-700 dark:text-gray-300 mb-1">
              No Announcements Found
            </p>
            <p className={bodySecondary}>
              {searchTerm
                ? "Try adjusting your search to find more announcements."
                : "There are no announcements for this course yet."}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AllAnnouncements;
